import { createObjectCsvStringifier } from 'csv-writer';
import { saveAs } from 'file-saver';
import React, { useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaFileExport } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import { useAnalytics } from '../../../helpers/analytics.helper';
import { bsTiny } from '../../../helpers/ui.helper';
import { useMembership } from '../../../hooks/users';
import { Analytics } from '../../../shared/types';
import { ReactTable } from '../../../types';

export const ExportButton: React.FC<{
  columns: ReactTable.Column[];
  data: ReadonlyArray<unknown>;
  exportFileName?: string;
  allowExport?: boolean;
}> = ({ allowExport, columns, data, exportFileName }) => {
  const { t } = useTranslation();
  const hasMembership = useMembership();
  const nagivate = useNavigate();

  const [queue] = useAnalytics();

  const exportToFile = useCallback(() => {
    if (!hasMembership) {
      return nagivate('/payment');
    }
    const getColumnId = (column, i): string => column.id || `${i}`;

    // ignore columns without accessor
    const effectiveColumns = columns
      .filter(
        column =>
          ['string', 'function'].includes(typeof column.accessor) ||
          ['string', 'function'].includes(typeof column.exportAccessor)
      )
      .filter(column => !column.disableExport);

    const rows = data.map(record => {
      const row = {};
      for (const [i, column] of effectiveColumns.entries()) {
        const { accessor, exportAccessor } = column;
        const effectiveAccessor = exportAccessor || accessor;
        const value =
          typeof effectiveAccessor === 'string'
            ? record[effectiveAccessor]
            : effectiveAccessor(record);
        row[getColumnId(column, i)] = value;
      }
      return row;
    });

    const headers = effectiveColumns.map((column, i) => ({
      id: getColumnId(column, i),
      title: typeof column.Header === 'string' ? column.Header : '[HTML]',
    }));
    const csvStringifier = createObjectCsvStringifier({
      header: headers,
    });

    const csvString =
      csvStringifier.getHeaderString() + csvStringifier.stringifyRecords(rows);

    saveAs(new Blob([csvString]), `${exportFileName}.csv`);

    queue(Analytics.ActionEnum.ExportTable, null, exportFileName);
  }, [columns, data, exportFileName, queue, nagivate, hasMembership]);

  return (
    allowExport && (
      <Button
        variant="outline-secondary"
        size={bsTiny}
        title={t('Export')}
        onClick={exportToFile}
      >
        <FaFileExport /> {t('Export')}
      </Button>
    )
  );
};
